"use client";

/**
 * Live open-review counts for the signed-in admin.
 *
 * GET /v1/public/global/admin/pending-work
 *
 * `tiles` keys match admin home route ids (`blogs`, `hub`). `total` is the
 * sum of those tiles and drives the nav indicator.
 */

import { createContext, useContext } from "react";
import { apiRequestExternal } from "./apiRequestExternal";
import { apiValidateArguments } from "./apiValidateArguments";
import { composerBrowserBaseUrl } from "./controllerRegistryAdmin";

export type AdminPendingWork = {
  tiles: Record<string, number>;
  details: Record<string, Record<string, number>>;
  total: number;
};

const emptyWork = (): AdminPendingWork => ({ tiles: {}, details: {}, total: 0 });

export function tilePendingCount(
  work: AdminPendingWork | null | undefined,
  tileId: string,
): number {
  const count = work?.tiles?.[tileId];
  return typeof count === "number" && count > 0 ? count : 0;
}

export async function fetchAdminPendingWork(accessToken: string): Promise<AdminPendingWork> {
  apiValidateArguments({ accessToken });
  const url = `${composerBrowserBaseUrl().replace(/\/$/, "")}/v1/public/global/admin/pending-work`;
  const result = await (apiRequestExternal as Function)(
    url,
    "GET",
    null,
    { Authorization: accessToken },
    false,
    false,
  );
  const body = result?.tiles ? result : result?.data;
  if (!body || typeof body !== "object") {
    return emptyWork();
  }
  return {
    tiles: body.tiles && typeof body.tiles === "object" ? body.tiles : {},
    details: body.details && typeof body.details === "object" ? body.details : {},
    total: typeof body.total === "number" ? body.total : 0,
  };
}

/** Count at `details[group][key]`, or 0 when absent or not positive. */
export function detailPendingCount(
  work: AdminPendingWork | null | undefined,
  group: string,
  key: string,
): number {
  const count = work?.details?.[group]?.[key];
  return typeof count === "number" && count > 0 ? count : 0;
}

export type AdminPendingWorkContextValue = {
  work: AdminPendingWork | null;
  /** Re-fetch the counts. Call after an approve, reject or edit that changes a queue. */
  refresh: () => Promise<void>;
};

export const AdminPendingWorkContext = createContext<AdminPendingWorkContextValue>({
  work: null,
  refresh: async () => {},
});

/**
 * Pending-work counts held by `AdminPendingWorkProvider` (one fetch per sign-in,
 * shared by every consumer). A failed request leaves the counts empty so the
 * indicators stay hidden.
 */
export function useAdminPendingWork(): AdminPendingWorkContextValue {
  return useContext(AdminPendingWorkContext);
}
